import * as THREE from "three";
import type { AssetLibrary } from "../../assets";
import type { KitPiece } from "./kitGeometry";
import { KIT_BUDGET, type Motion } from "./types";

/** A kit GLB's pieces, loaded on demand and cached by asset (kit/runtime.ts SkinKits.pieces). */
export class KitPieces {
  private static readonly cache = new Map<string, Map<string, KitPiece>>();
  private static readonly pending = new Set<string>();

  /**
   * The pieces of a kit GLB (each top-level node is one prop, merged into one geometry in its own frame), or null
   * until it is loaded — asking starts the load.
   */
  static of(L: AssetLibrary, asset: string, onLoaded: () => void): Map<string, KitPiece> | null {
    const cached = KitPieces.cache.get(asset);
    if (cached) return cached;
    if (!L.has(`hq:${asset}`)) {
      if (L.ships(asset) && !KitPieces.pending.has(asset)) {
        KitPieces.pending.add(asset);
        void L.ensure([asset]).then(() => { KitPieces.pending.delete(asset); onLoaded(); });
      }
      return null;
    }
    const root = L.clone(`hq:${asset}`);
    root.updateMatrixWorld(true);
    const out = new Map<string, KitPiece>();
    const top = root.children.length === 1 && !(root.children[0] as THREE.Mesh).isMesh && root.children[0].children.length > 1 && !root.children[0].userData.td_piece ? root.children[0].children : root.children;
    for (const node of top) {
      const piece = extractPiece(node);
      if (piece) out.set(piece.name, piece);
    }
    KitPieces.cache.set(asset, out);
    return out;
  }
}

/** One prop node of a kit GLB → its merged geometry in the node's own frame (positions, normals, uv, cloth weight). */
function extractPiece(node: THREE.Object3D): KitPiece | null {
  const meshes: THREE.Mesh[] = [];
  node.traverse((o) => { if ((o as THREE.Mesh).isMesh) meshes.push(o as THREE.Mesh); });
  if (meshes.length === 0) return null;
  const toNode = new THREE.Matrix4().copy(node.matrixWorld).invert();
  const pos: number[] = [], nrm: number[] = [], uv: number[] = [], w: number[] = [], idx: number[] = [];
  const v = new THREE.Vector3(), m = new THREE.Matrix4(), n3 = new THREE.Matrix3();
  for (const mesh of meshes) {
    const g = mesh.geometry, P = g.getAttribute("position"), N = g.getAttribute("normal"), U = g.getAttribute("uv"), C = g.getAttribute("color");
    m.multiplyMatrices(toNode, mesh.matrixWorld);
    n3.getNormalMatrix(m);
    const base = pos.length / 3;
    for (let i = 0; i < P.count; i++) {
      v.fromBufferAttribute(P, i).applyMatrix4(m); pos.push(v.x, v.y, v.z);
      if (N) { v.fromBufferAttribute(N, i).applyMatrix3(n3).normalize(); nrm.push(v.x, v.y, v.z); } else nrm.push(0, 1, 0);
      if (U) uv.push(U.getX(i), U.getY(i)); else uv.push(0, 0);
      w.push(C ? C.getX(i) : 0);
    }
    if (g.index) for (let i = 0; i < g.index.count; i++) idx.push(g.index.getX(i) + base);
    else for (let i = 0; i < P.count; i++) idx.push(i + base);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geometry.setAttribute("normal", new THREE.Float32BufferAttribute(nrm, 3));
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  geometry.setAttribute("kitWeight", new THREE.Float32BufferAttribute(w, 1));
  geometry.setIndex(idx);
  const raw = node.userData.td_motion;
  let motion: Motion | undefined;
  try { motion = typeof raw === "string" ? (JSON.parse(raw) as Motion) : (raw as Motion | undefined); } catch { motion = undefined; }
  const tris = idx.length / 3;
  if (tris > KIT_BUDGET.TrisPerPiece) console.warn(`skin kit piece ${node.name}: ${tris} triangles (budget ${KIT_BUDGET.TrisPerPiece})`);
  return { name: node.name, geometry, material: meshes[0].material as THREE.MeshStandardMaterial, motion, tris };
}
